import React, { useMemo, useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MoodboardItem } from '../types/moodboard';
interface ThematicGroup {
  id: string;
  label: string;
  description: string;
  matchTags: string[];
  matchTypes?: MoodboardItem['type'][];
}
const THEMATIC_GROUPS: ThematicGroup[] = [{
  id: 'materie',
  label: 'MATERIE & TESSUTI',
  description: 'Selezione materiali, tessuti, fantasie e lavorazioni per la collezione',
  matchTags: ['TEXTURE', 'TESSUTO', 'LANA', 'TWEED', 'FANTASIA', 'SETA', 'COTONE', 'LINO', 'PELLE', 'DENIM', 'GABARDINA', 'CASHMERE', 'VELLUTO', 'JERSEY', 'FILATO', 'MATERIA']
}, {
  id: 'palette',
  label: 'PALETTE COLORE',
  description: 'Cartella colori stagionale, combinazioni cromatiche e toni',
  matchTags: ['PALETTE BASE', 'MONOCROMO', 'TONI TERRA', 'PALETTE ACCENTO', 'COLORI', 'PALETTE', 'CROMIA', 'TONI'],
  matchTypes: ['color_palette']
}, {
  id: 'costruzione',
  label: 'COSTRUZIONE & FIT',
  description: 'Volumi, tagli, silhouette, layering e proporzioni dei capi',
  matchTags: ['SARTORIA', 'LAYERING', 'TRE PEZZI', 'ABITI', 'CHINO', 'GIACCA', 'COSTRUZIONE', 'FIT', 'SILHOUETTE', 'VOLUME', 'PROPORZIONI', 'DESTRUTTURATO', 'OVERSIZE', 'SLIM']
}, {
  id: 'dettagli',
  label: 'DETTAGLI & FINITURE',
  description: 'Bottoni, cuciture, accessori, hardware e lavorazioni di dettaglio',
  matchTags: ['FINITURE', 'DETTAGLIO', 'DETTAGLI', 'OPACO', 'BOTTONI', 'CUCITURE', 'HARDWARE', 'ACCESSORI', 'IMPUNTURE', 'FODERE', 'ASOLE']
}, {
  id: 'mood',
  label: 'MOOD & VISIONE',
  description: 'Direzione estetica, riferimenti culturali e mood della collezione',
  matchTags: ['DIREZIONE', 'MINIMALISMO', 'AUDACIA', 'MOOD', 'VISIONE', 'ESTETICA', 'ISPIRAZIONE', 'CONCETTO']
}, {
  id: 'benchmark',
  label: 'BENCHMARK & MERCATO',
  description: 'Competitor, brand di riferimento, fiere e trend di mercato',
  matchTags: ['RIFERIMENTO', 'BRAND', 'VELASCA', 'PITTI', 'BENCHMARK', 'COMPETITOR', 'MERCATO', 'TREND', 'FIERA', 'STREET STYLE'],
  matchTypes: ['link']
}];
interface GroupingViewProps {
  items: MoodboardItem[];
}
export function GroupingView({
  items
}: GroupingViewProps) {
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());
  const toggleFilter = (groupId: string) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };
  // Compute which items belong to each thematic group
  const groupedItems = useMemo(() => {
    const result: Record<string, MoodboardItem[]> = {};
    THEMATIC_GROUPS.forEach(group => {
      const matchingItems = items.filter(item => {
        // Match by type
        if (group.matchTypes && group.matchTypes.includes(item.type)) {
          return true;
        }
        // Match by tags (case-insensitive)
        const itemTagsUpper = item.tags.map(t => t.toUpperCase());
        return group.matchTags.some(matchTag => itemTagsUpper.some(itemTag => itemTag === matchTag || itemTag.includes(matchTag) || matchTag.includes(itemTag)));
      });
      result[group.id] = matchingItems;
    });
    return result;
  }, [items]);
  // Only show groups that have items
  const activeGroups = THEMATIC_GROUPS.filter(group => groupedItems[group.id].length > 0);
  // Filter visible groups
  const visibleGroups = activeFilters.size === 0 ? activeGroups : activeGroups.filter(group => activeFilters.has(group.id));
  return <div className="w-full h-full min-h-[calc(100vh-4rem)] sm:min-h-[calc(100vh-5rem)] bg-[#FAFAFA] flex flex-col" data-id="element-130">
      {/* Filter Bar */}
      <div className="sticky top-0 z-10 bg-[#FAFAFA]/90 backdrop-blur-md border-b border-border px-4 sm:px-8 py-4" data-id="element-131">
        <div className="flex items-center gap-4" data-id="element-132">
          <span className="font-heading text-[10px] tracking-[0.2em] uppercase text-[#666] whitespace-nowrap" data-id="element-133">
            FILTRA PER:
          </span>
          <div className="flex-1 overflow-x-auto no-scrollbar flex items-center gap-2 pb-1" data-id="element-134">
            {activeGroups.map(group => {
            const isActive = activeFilters.has(group.id);
            const count = groupedItems[group.id].length;
            return <button key={group.id} onClick={() => toggleFilter(group.id)} className={`whitespace-nowrap font-heading text-[9px] tracking-[0.15em] uppercase px-3 py-1.5 border transition-colors flex items-center gap-1.5 ${isActive ? 'bg-black text-white border-black' : 'bg-white text-[#666] border-border hover:border-[#999] hover:text-black'}`} data-id="element-135">
                  {group.label}
                  <span className={isActive ? 'text-white/60' : 'text-[#999]'} data-id="element-136">
                    {count}
                  </span>
                </button>;
          })}
          </div>
        </div>
      </div>

      {/* Grouped Sections */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-8 space-y-14 pb-24" data-id="element-137">
        <AnimatePresence mode="popLayout" data-id="element-138">
          {visibleGroups.map(group => <motion.div key={group.id} layout initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          duration: 0.4,
          ease: [0.22, 1, 0.36, 1]
        }} className="space-y-4" data-id="element-139">
              <div className="border-b border-border pb-3" data-id="element-140">
                <h2 className="font-heading text-[12px] tracking-[0.25em] uppercase text-black" data-id="element-141">
                  {group.label}
                  <span className="text-[#999] ml-2" data-id="element-142">
                    ({groupedItems[group.id].length})
                  </span>
                </h2>
                <p className="text-[11px] text-[#999] font-sans mt-1" data-id="element-143">
                  {group.description}
                </p>
              </div>

              <div className="flex overflow-x-auto no-scrollbar gap-4 pb-4" data-id="element-144">
                {groupedItems[group.id].map(item => <CompactCard key={`${group.id}-${item.id}`} item={item} data-id="element-145" />)}
              </div>
            </motion.div>)}
        </AnimatePresence>

        {visibleGroups.length === 0 && <div className="text-center py-20" data-id="element-146">
            <p className="font-heading text-[12px] tracking-[0.2em] uppercase text-[#666]" data-id="element-147">
              NESSUN GRUPPO TROVATO
            </p>
          </div>}
      </div>
    </div>;
}
function CompactCard({
  item
}: {
  item: MoodboardItem;
}) {
  const renderPreview = () => {
    switch (item.type) {
      case 'image':
        return <img src={item.content} alt={item.title || 'Preview'} className="w-full h-full object-cover" loading="lazy" data-id="element-148" />;
      case 'note':
        return <div className="w-full h-full p-4 flex items-center justify-center bg-white" data-id="element-149">
            <p className="text-[10px] font-sans leading-relaxed text-[#1A1A1A] line-clamp-4 text-center" data-id="element-150">
              "{item.content}"
            </p>
          </div>;
      case 'link':
        return <div className="w-full h-full p-4 flex items-center justify-center bg-white" data-id="element-151">
            <p className="font-heading text-[9px] tracking-[0.15em] uppercase text-center text-[#666] line-clamp-3" data-id="element-152">
              {item.title || 'REFERENCE LINK'}
            </p>
          </div>;
      case 'color_palette':
        const colors = item.content.split(',').map(c => c.trim()).filter(c => c.startsWith('#'));
        return <div className="w-full h-full flex" data-id="element-153">
            {colors.map((color, idx) => <div key={idx} className="flex-1 h-full" style={{
            backgroundColor: color
          }} data-id="element-154" />)}
          </div>;
      default:
        return null;
    }
  };
  return <div className="flex-none w-[160px] sm:w-[200px] group" data-id="element-155">
      <div className="w-full h-[120px] sm:h-[140px] border border-border bg-white overflow-hidden group-hover:border-[#999] transition-colors duration-300" data-id="element-156">
        {renderPreview()}
      </div>
      <div className="mt-2 px-1" data-id="element-157">
        <p className="font-heading text-[9px] tracking-[0.15em] uppercase text-black truncate" data-id="element-158">
          {item.title || item.type}
        </p>
      </div>
    </div>;
}